import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, PlusSquare, FileStack, Users, Building2, LogOut, Database, Menu, CalendarRange, Home, LayoutGrid, ClipboardList, Send, Inbox, BarChart3, Factory, FileText, Activity, Flame, Wrench, Disc3, FileClock, Sparkles, ShieldAlert, ClipboardCheck, History, AlertTriangle, ShieldOff, KeyRound, MonitorSmartphone, Link2, ScrollText } from "lucide-react";import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { TrustedDevicesDialog } from "@/components/TrustedDevicesDialog";

const roleLabel = { admin: "Administrator", projektleder: "Projektleder", medarbejder: "Medarbejder", kvalitet: "Kvalitet", indkoeb: "Indkøb" };

function NavItem({ to, icon: Icon, label, end, testid, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 mx-3 px-3 py-2.5 text-sm font-medium rounded-xl [transition-property:color,background-color] duration-200 ${
          isActive
            ? "bg-[#F1DEDA] text-[#A83232]"
            : "text-stone-600 hover:bg-[#F3F1EC] hover:text-stone-900"
        }`}>
      <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} /> {label}
    </NavLink>
  );
}

function Sidebar({ user, isAdmin, onNav, onLogout, onLogoutAll, onChangePassword, onDevices, prefix, backupOwner }) {
  const id = (name) => `${prefix}${name}`;
  const has = (d) => isAdmin || (user?.departments || []).includes(d);
  const isPM = user?.role === "admin" || user?.role === "projektleder";
  const deps = user?.departments || [];
  const prodOnly = !isAdmin && deps.includes("produktion") && !["salg", "indkoeb", "kvalitet"].some((d) => deps.includes(d));
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center px-6 h-20 shrink-0">
        <img src="/bsm-logo.png" alt="BSM A/S — vision in steel" data-testid="sidebar-brand-logo" className="h-9 w-auto" />
      </div>
      <nav className="flex-1 py-2 overflow-y-auto">
        <NavItem to="/" end icon={Home} label="Forside" testid={id("nav-home")} onClick={onNav} />

        {!prodOnly && (
        <>
        <p className="label-caps px-6 mb-2 mt-6">Salg</p>
        <NavItem to="/projekter" icon={LayoutGrid} label="Projektoversigt" testid={id("nav-dashboard")} onClick={onNav} />
        <NavItem to="/ressourceplan" icon={CalendarRange} label="Planlægning" testid={id("nav-resource-plan")} onClick={onNav} />
        {has("salg") && isPM && <NavItem to="/opret-projekt" icon={PlusSquare} label="Opret nyt projekt" testid={id("nav-create")} onClick={onNav} />}
        {isPM && <NavItem to="/admin/kunder" icon={Building2} label="Kunder" testid={id("nav-customers")} onClick={onNav} />}

        <p className="label-caps px-6 mb-2 mt-6">Kvalitet</p>
        <NavItem to="/kvalitet" end icon={LayoutGrid} label="Oversigt" testid={id("nav-kvalitet")} onClick={onNav} />
        <NavItem to="/kvalitet/planlaegning" icon={CalendarRange} label="Planlægning" testid={id("nav-kvalitet-plan")} onClick={onNav} />
        <NavItem to="/reklamationer" icon={ShieldAlert} label="Reklamationer" testid={id("nav-reklamationer")} onClick={onNav} />
        <NavItem to="/kvalitet/reklamationsanalyse" icon={Sparkles} label="Reklamationsanalyse" testid={id("nav-rekl-analyse")} onClick={onNav} />
        <NavItem to="/kvalitet/opret" icon={PlusSquare} label="Opret ny case" testid={id("nav-kvalitet-create")} onClick={onNav} />
        {(isAdmin || deps.includes("kvalitet")) && <NavItem to="/kvalitet/audit" end icon={ClipboardCheck} label="Auditplan" testid={id("nav-audit-plan")} onClick={onNav} />}
        {(isAdmin || deps.includes("kvalitet")) && <NavItem to="/kvalitet/audit/historik" icon={History} label="Audithistorik" testid={id("nav-audit-history")} onClick={onNav} />}
        <NavItem to="/kvalitet/audit/fund" icon={AlertTriangle} label="Afvigelser og fund" testid={id("nav-audit-findings")} onClick={onNav} />

        <p className="label-caps px-6 mb-2 mt-6">Indkøb</p>
        <NavItem to="/indkoeb" end icon={LayoutGrid} label="Oversigt" testid={id("nav-indkoeb")} onClick={onNav} />
        <NavItem to="/indkoeb/planlaegning" icon={CalendarRange} label="Planlægning" testid={id("nav-indkoeb-plan")} onClick={onNav} />
        {isPM && <NavItem to="/indkoeb/leverandoerer" icon={Building2} label="Leverandører" testid={id("nav-suppliers")} onClick={onNav} />}
        <NavItem to="/indkoeb/reklamationer" icon={ShieldAlert} label="Reklamationer" testid={id("nav-indkoeb-rekl")} onClick={onNav} />
        <NavItem to="/indkoeb/reklamationsanalyse" icon={Sparkles} label="Reklamationsanalyse" testid={id("nav-indkoeb-rekl-analyse")} onClick={onNav} />
        <NavItem to="/indkoeb/opret" icon={PlusSquare} label="Opret ny case" testid={id("nav-indkoeb-create")} onClick={onNav} />
        </>
        )}

        <p className="label-caps px-6 mb-2 mt-6">Produktion</p>
        <NavItem to="/produktion/svejsning" icon={Flame} label="Svejseafdeling" testid={id("nav-svejsning")} onClick={onNav} />
        <NavItem to="/produktion/montage" icon={Wrench} label="Montageafdeling" testid={id("nav-montage")} onClick={onNav} />
        <NavItem to="/produktion/slibning" icon={Disc3} label="Slibeafdeling" testid={id("nav-slibning")} onClick={onNav} />

        {isAdmin && (
          <>
            <p className="label-caps px-6 mb-2 mt-6">Ledelse</p>
            <NavItem to="/ledelse" icon={ClipboardCheck} label="Ledelsens evaluering" testid={id("nav-le")} onClick={onNav} />
            <NavItem to="/admin/total-planlaegning" icon={CalendarRange} label="Total planlægning" testid={id("nav-total-plan")} onClick={onNav} />
            <NavItem to="/admin/rapporter" icon={BarChart3} label="Ledelsesrapport" testid={id("nav-reports")} onClick={onNav} />
            <NavItem to="/admin/aktivitet" icon={Activity} label="Aktivitetslog" testid={id("nav-activity")} onClick={onNav} />
            <NavItem to="/admin/dokument-log" icon={FileClock} label="Dokument-log" testid={id("nav-doc-log")} onClick={onNav} />
            <NavItem to="/admin/skabeloner" icon={FileStack} label="Projektskabeloner" testid={id("nav-templates")} onClick={onNav} />
            <NavItem to="/admin/brugere" icon={Users} label="Brugere & roller" testid={id("nav-users")} onClick={onNav} />
            <NavItem to="/admin/delingslinks" icon={Link2} label="Delingslinks" testid={id("nav-share-links")} onClick={onNav} />
            {backupOwner && <NavItem to="/admin/backup" icon={Database} label="Backup" testid={id("nav-backup")} onClick={onNav} />}
            <NavItem to="/admin/audit-log" icon={ScrollText} label="Audit-log" testid={id("nav-audit-log")} onClick={onNav} />
          </>
        )}
      </nav>
      <div className="border-t border-[#EBE8E3] p-4 mx-2">
        <div className="mb-3 px-2">
          <p className="text-sm font-semibold text-stone-900 truncate" data-testid={id("current-user-name")}>{user?.name}</p>
          <p className="text-[11px] text-[#A83232] font-semibold uppercase tracking-[0.12em] mt-0.5" data-testid={id("current-user-role")}>{roleLabel[user?.role] || user?.role}</p>
        </div>
        <Button variant="ghost" size="sm" data-testid={id("logout-btn")} onClick={onLogout}
          className="w-full justify-start text-stone-500 hover:text-stone-900 hover:bg-[#F3F1EC] rounded-xl">
          <LogOut className="h-4 w-4 mr-2" strokeWidth={1.75} /> Log ud
        </Button>
        {user?.account_type === "shared" && (
          <p className="text-[11px] text-stone-400 px-3 py-1" data-testid={id("shared-account-note")}>Fælleskonto – adgangskoden administreres af administrator.</p>
        )}
        {user?.account_type !== "shared" && <>
        <Button variant="ghost" size="sm" data-testid={id("change-password-btn")} onClick={onChangePassword}
          className="w-full justify-start text-stone-400 hover:text-stone-900 hover:bg-[#F3F1EC] rounded-xl text-xs">
          <KeyRound className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Skift adgangskode
        </Button>
        <Button variant="ghost" size="sm" data-testid={id("trusted-devices-btn")} onClick={onDevices}
          className="w-full justify-start text-stone-400 hover:text-stone-900 hover:bg-[#F3F1EC] rounded-xl text-xs">
          <MonitorSmartphone className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Betroede enheder
        </Button>
        <Button variant="ghost" size="sm" data-testid={id("logout-all-btn")} onClick={onLogoutAll}
          className="w-full justify-start text-stone-400 hover:text-stone-900 hover:bg-[#F3F1EC] rounded-xl text-xs">
          <ShieldOff className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Log ud på alle enheder
        </Button>
        </>}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, logoutAll } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const [backupOwner, setBackupOwner] = useState(false);
  useEffect(() => { if (isAdmin) api.get("/backup/status").then((r) => setBackupOwner(!!r.data?.is_owner)).catch(() => {}); }, [isAdmin]);
  const doLogout = () => { logout(); nav("/login"); };
  const doLogoutAll = async () => { await logoutAll(); toast.success("Du er logget ud på alle enheder"); nav("/login"); };
  const [chpwOpen, setChpwOpen] = useState(false);
  const [devOpen, setDevOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#FBFAF8]">
      <aside className="no-print hidden lg:flex w-64 bg-white border-r border-[#EBE8E3] flex-col fixed inset-y-0 z-30">
        <Sidebar backupOwner={backupOwner} user={user} isAdmin={isAdmin} onNav={() => {}} onLogout={doLogout} onLogoutAll={doLogoutAll} onChangePassword={() => setChpwOpen(true)} onDevices={() => setDevOpen(true)} prefix="" />
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setOpen(false)} />}
      <aside className={`no-print lg:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-[#EBE8E3] z-50 transform transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <Sidebar backupOwner={backupOwner} user={user} isAdmin={isAdmin} onNav={() => setOpen(false)} onLogout={doLogout} onLogoutAll={doLogoutAll} onChangePassword={() => { setOpen(false); setChpwOpen(true); }} onDevices={() => { setOpen(false); setDevOpen(true); }} prefix="m-" />
      </aside>

      <div className="flex-1 lg:ml-64 print:ml-0 flex flex-col min-w-0">
        <header className="no-print lg:hidden h-16 border-b border-[#EBE8E3] bg-white flex items-center px-4 gap-3 sticky top-0 z-20">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          <img src="/bsm-logo.png" alt="BSM A/S — vision in steel" data-testid="mobile-brand-logo" className="h-7 w-auto" />
        </header>
        <main className="flex-1 px-5 py-8 sm:px-8 sm:py-10 lg:px-12 max-w-[1500px] w-full"><Outlet /></main>
      </div>
      <ChangePasswordDialog open={chpwOpen} onOpenChange={setChpwOpen} />
      <TrustedDevicesDialog open={devOpen} onOpenChange={setDevOpen} />
    </div>
  );
}
